import { effect, inject, Injectable, signal } from '@angular/core';
import { CreatorModerationStatusEnum, CreatorResponseDto } from '../../../../common/general-types/creators.dto';
import { HttpClient } from '@angular/common/http';
import { FirebaseService } from '../../../../common/firebase/firebase.service';
import { environment } from '../../../../../environments/environment';
import { firstValueFrom, Observable, Subscription, take, tap } from 'rxjs';
import { ActiveFiltersModel, FilterCreatorsDto } from '../../../../common/general-types/filters.dto';
import { CurrentUserService } from '../../../../common/current-user/current-user.service';

import { interestsList } from '../../../../common/app-config/filter-models/interests';
import { countriesList } from '../../../../common/app-config/filter-models/countries';
import { genderOptions } from '../../../../common/app-config/filter-models/gender';
import { influencerSizes } from '../../../../common/app-config/filter-models/tiktok-influencers';
import { creatorSortOptions } from '../../../../common/app-config/filter-models/sort';
import { platformsList } from '../../../../common/app-config/filter-models/platforms';
import { verifiedOptions } from '../../../../common/app-config/filter-models/verified';
import { PurpleCreatorCardVideo } from '../../../creators-v2/molecules/creator-card-purple/creator-card-purple.component';
import { VideoCreatorResponseDto, VideoResponseDto, VideoResponseWithBriefDto } from '../../../../common/general-types/videos.dto';
import { RecruitGroupService } from '../../../../services/recruit-group/recruit-group.service';
import { BriefApplicationResponseDto, BriefAudienceType } from '../../../../common/general-types/briefs.dto';
import { CreatorStatsService } from '../../../../services/creator-stats/creator-stats.service';
import { CreatorService } from '../../../../services/creator/creator.service';

export type PortfolioNavItem = {
  creator: CreatorResponseDto;
  application?: BriefApplicationResponseDto;
};

@Injectable({
  providedIn: 'root'
})
export class MagicSearchService {

  previewVideo = signal<VideoCreatorResponseDto | VideoResponseDto | undefined>(undefined);
  previewVideoPlaylist = signal<(VideoCreatorResponseDto | VideoResponseDto)[] | undefined>(undefined);

  clearPreviewVideo(): void {
    this.previewVideo.set(undefined);
    this.previewVideoPlaylist.set(undefined);
  }

  /** Creator card with open "+X more" interests popover (one at a time). */
  openInterestsPopoverCreatorId = signal<string | null>(null);

  /** Creator card with open "Read more" bio popover (one at a time). */
  openBioPopoverCreatorId = signal<string | null>(null);

  /** Search Vars */
  searchColour = signal<'pink' | 'blue' | 'purple'>('pink');
  selectedPointer = signal<{ label: string, class: string, description: string, help: string }>({ label: 'lucky:', class: 'bg-pink-50 text-pink-500 ring-pink-700', description: "Tell us what you're looking for...", help: "Search for anything ... You're feeling lucky!" });
  searchQuery = signal<string | undefined>('');
  isSearching = signal<boolean>(false);
  pageNumber = signal<number>(0);
  pageSize = signal<number>(12);

  searchMode = signal<'search' | 'filters' | 'foryou'>('search');
  loadingResults = signal<boolean>(false);
  /** Search Vars */

  /** Search Results Vars */
  blueSearchResults = signal<CreatorResponseDto[] | undefined>(undefined);
  /** Scroll offset for creators-v4-filter results; survives route remounts (e.g. admin profile). */
  blueSearchResultsScrollTop = 0;
  purpleSearchResults = signal<PurpleCreatorCardVideo[] | undefined>(undefined);
  pinkSearchResults = signal<CreatorResponseDto[] | undefined>(undefined);

  goldOnboardingSearchResults = signal<GoldSearchResponseDto | undefined>(undefined);

  forYouSearchResults = signal<CreatorResponseDto[] | undefined>(undefined);
  /** Search Results Vars */



  /** Pointer Vars */
  // defaultPointer: SearchPointer = { label: 'lucky:', class: 'bg-pink-50 text-pink-500 ring-pink-700', description: "Tell us what you're looking for...", help: "Search for anything ... You're feeling lucky!" };
  defaultPointer: SearchPointer = { label: 'user id:', class: 'bg-blue-50 dark:bg-blue-900/30 text-blue-500 dark:text-blue-400 ring-blue-700', description: 'Search by User ID', help: 'Search by user id' }

  pink_pointers: SearchPointer[] = [
    { label: 'lucky:', class: 'bg-pink-50 text-pink-500 ring-pink-700', description: "Tell us what you're looking for...", help: "Search for anything ... You're feeling lucky!" },
    { label: 'for me:', class: 'bg-pink-50 text-pink-500 ring-pink-700', description: "Based on your brand & products...", help: "We'll find creators who have mentioned your brand & products (or similar) in their content." },
  ];
  blue_pointers: SearchPointer[] = [
    { label: 'name:', class: 'bg-blue-50 dark:bg-blue-900/30 text-blue-500 dark:text-blue-400 ring-blue-700', description: 'Search by name', help: 'Search by name' },
    { label: 'handle:', class: 'bg-blue-50 dark:bg-blue-900/30 text-blue-500 dark:text-blue-400 ring-blue-700', description: 'Search by handle', help: "Search by handle, we will look for this handle across all social networks." },
    { label: 'email:', class: 'bg-blue-50 dark:bg-blue-900/30 text-blue-500 dark:text-blue-400 ring-blue-700', description: 'Search by email', help: 'Search by email' },
    { label: 'user id:', class: 'bg-blue-50 dark:bg-blue-900/30 text-blue-500 dark:text-blue-400 ring-blue-700', description: 'Search by User ID', help: 'Search by user id' },
  ];
  purple_pointers: SearchPointer[] = [
    { label: 'persona:', class: 'bg-primary/10 dark:bg-primary/25 text-accent ring-primary/30', description: 'Describe the creator... (AI Powered)', help: "Describe a creator's person; their look, style, personality or channel-defining features. We'll find creators who match your description." },
    { label: 'video:', class: 'bg-primary/10 dark:bg-primary/25 text-accent ring-primary/30', description: 'Find _ in a video... (AI Powered)', help: "Describe something that you saw or heard in a video, we'll find the scene and creator who produced the video." },
    { label: 'action:', class: 'bg-primary/10 dark:bg-primary/25 text-accent ring-primary/30', description: 'Videos where this action is displayed (AI Powered)', help: "Describe an action that you would like to see in the video, we'll find the creator who has displayed this action in their video." },
    { label: 'industry:', class: 'bg-primary/10 dark:bg-primary/25 text-accent ring-primary/30', description: 'Search by Inustry Keyword (AI Powered)', help: "You can search for keywords by Industry like 'Transport', 'Fashion', 'Wellness' and the like. we'll find the creator who has displayed this action in their video." },
  ];
  /** Pointer Vars */
  


  private http = inject(HttpClient);
  private firebaseService = inject(FirebaseService);
  private currentUserService = inject(CurrentUserService);
  private recruitGroupService = inject(RecruitGroupService);
  private creatorStatsService = inject(CreatorStatsService);
  private creatorService = inject(CreatorService);
  private blueSearchRequest?: Subscription;

  /** Drops a blue search that has not returned, so a later result cannot overwrite a newer query. */
  cancelInFlightBlueSearch(): void {
    this.blueSearchRequest?.unsubscribe();
    this.blueSearchRequest = undefined;
  }


  /** Filters Vars */
  initFiltersObject: FilterCreatorsDto = {
    skip: 0,
    take: 12,
    exclusive_to_business_id: this.currentUserService.focussedBrand?.id || ''
  }

  activeFilters: ActiveFiltersModel = {
    ageOptions: { min: 0, max: 60 },
    interestsOptions: interestsList,
    countryOptions: countriesList,
    genderOptions: genderOptions,
    tiktokSizeOptions: influencerSizes,
    instagramSizeOptions: influencerSizes,
    youtubeSizeOptions: influencerSizes,
    snapchatSizeOptions: influencerSizes,
    sortOptions: creatorSortOptions,
    platformOptions: platformsList,
    isVerifiedOptions: verifiedOptions,
    homeTownSearchTerm: '',
  };

  filtersOpen = signal<boolean>(false);
  /** Mirrors active filter options for UI; must be set before any child reads it (e.g. toolbar renders before filter drawer effect runs). */
  fancyActiveFilters: ActiveFiltersModel = this.activeFilters;
  fancyInterestsSelected: number = 0;

  fancyTotalFilters = signal<number>(0);

  fancyActiveFiltersSignal = signal<ActiveFiltersModel | undefined>(undefined);
  activeFiltersSignal = signal<ActiveFiltersModel>(this.activeFilters);
  filtersObjectSignal = signal<FilterCreatorsDto>(this.initFiltersObject);

  appliedFilters: any = {};
  appliedSearchTerm: string | undefined;
  previousFilters: any = {};

  /** Clears keyword input state so the same term can be searched again. */
  clearKeywordSearch(): void {
    this.searchQuery.set('');
    this.appliedSearchTerm = undefined;
  }
  /**
   * When set (monitor tab `mc-search-creators`), merged into every blue-search filter payload
   * so drawer/pagination/keyword paths all include `socialMonitoringBriefId`.
   */
  socialMonitoringBriefIdContext = signal<string | undefined>(undefined);
  /**
   * When find-creators is opened in a brief context, SHOUT+ creator briefs skip the ready-only moderation filter.
   */
  findCreatorsBriefAudience = signal<BriefAudienceType | number | string | undefined>(undefined);
  /** Brief being invited to (set by creators-v4-filter when route has a brief UUID). */
  findCreatorsBriefId = signal<string | undefined>(undefined);
  /** Filters Vars */


  /** Creator Profile Vars */
  portfolioCreatorId = signal<string | undefined>(undefined);
  portfolioCreator = signal<CreatorResponseDto | undefined>(undefined);
  portfolioBriefApplication = signal<BriefApplicationResponseDto | undefined>(undefined);
  /** Creators/applicants available for left/right navigation in the profile modal. */
  portfolioNavItems = signal<PortfolioNavItem[]>([]);
  /** Optional Socials sub-tab to open when the profile modal launches. */
  portfolioSocialTab = signal<'tiktok' | 'instagram' | 'youtube' | 'snapchat' | undefined>(undefined);
  /** Creator Profile Vars */

  openCreatorPortfolio(
    creator: CreatorResponseDto,
    options?: {
      application?: BriefApplicationResponseDto;
      navItems?: PortfolioNavItem[];
      socialTab?: 'tiktok' | 'instagram' | 'youtube' | 'snapchat';
    },
  ): void {
    this.portfolioCreatorId.set(creator.blazeFirebaseUid);
    this.portfolioCreator.set(creator);
    this.portfolioBriefApplication.set(options?.application);
    this.portfolioNavItems.set(options?.navItems ?? []);
    this.portfolioSocialTab.set(options?.socialTab);
  }

  clearCreatorPortfolio(): void {
    this.portfolioCreatorId.set(undefined);
    this.portfolioCreator.set(undefined);
    this.portfolioBriefApplication.set(undefined);
    this.portfolioNavItems.set([]);
    this.portfolioSocialTab.set(undefined);
  }

  showAdjacentPortfolio(direction: -1 | 1): boolean {
    const items = this.portfolioNavItems();
    const currentId = this.portfolioCreatorId();
    if (!items.length || !currentId) {
      return false;
    }
    const currentIndex = items.findIndex(
      (item) => item.creator.blazeFirebaseUid === currentId,
    );
    if (currentIndex < 0) {
      return false;
    }
    const nextIndex = currentIndex + direction;
    if (nextIndex < 0 || nextIndex >= items.length) {
      return false;
    }
    const next = items[nextIndex];
    this.portfolioCreatorId.set(next.creator.blazeFirebaseUid);
    this.portfolioCreator.set(next.creator);
    this.portfolioBriefApplication.set(next.application);
    return true;
  }

  constructor() {
    this.currentUserService.focussedBrand$.subscribe((brand) => {
      console.log('👮🏻‍♂️👮🏻‍♂️ Magic Search Effect -> Focussed Brand:', brand);
      this.initFiltersObject.exclusive_to_business_id = brand?.id || '';
      if (brand && brand.id) {
        this.initFiltersObject.exclusive_to_business_id = brand.id;
        // this.kickOffPinkSearchForMe(); => TO-DO!! - Un-comment
      }
      if (this.firebaseService.accessToken) {
        this.recruitGroupService.initRecruitmentGroups();
      }
    });
  }

  private mergeSocialMonitoringBriefIntoBlueFilters(filters: any): any {
    const id = this.socialMonitoringBriefIdContext();
    if (!id) {
      return filters;
    }
    if (filters === undefined || filters === null) {
      return { socialMonitoringBriefId: id };
    }
    return { ...filters, socialMonitoringBriefId: id };
  }

  private shouldBypassReadyOnlyModerationFilter(): boolean {
    return this.isShoutPlusFindCreatorsAudience();
  }

  /** Invite Creators for a SHOUT+ brief — list must be business / SHOUT+ creators only. */
  isShoutPlusFindCreatorsAudience(): boolean {
    const audience = this.findCreatorsBriefAudience();
    return audience === BriefAudienceType.SHOUT_PLUS_CUSTOMERS || audience === 'shout_plus';
  }

  applyFindCreatorsModerationFilter<T extends FilterCreatorsDto | Record<string, unknown>>(filters: T): T {
    const resolved = { ...filters } as T & FilterCreatorsDto;

    if (this.shouldBypassReadyOnlyModerationFilter()) {
      resolved.limitModerationStatus = null;
    } else {
      resolved.limitModerationStatus = [CreatorModerationStatusEnum.readyToStart];
    }

    return resolved;
  }

  /**
   * Build filters for `/creators/filter/shout-plus-creators` (shaas18 find-creators parity).
   */
  buildShoutPlusInviteFilters(
    filter?: FilterCreatorsDto | Record<string, unknown> | null,
    take?: number,
    skip?: number,
    queryText?: string,
  ): FilterCreatorsDto {
    const shoutPlusFilters: FilterCreatorsDto = {
      ...(filter ?? {}),
      take: take ?? this.pageSize(),
      skip: skip ?? 0,
    };

    shoutPlusFilters.businessAccountId =
      shoutPlusFilters.businessAccountId
      ?? this.currentUserService.focussedBrand?.id;

    delete shoutPlusFilters.exclusive_to_business_id;
    delete (shoutPlusFilters as FilterCreatorsDto & { exclusive_to_agency_id?: unknown }).exclusive_to_agency_id;
    delete (shoutPlusFilters as FilterCreatorsDto & { sort?: unknown }).sort;

    const briefId = this.findCreatorsBriefId();
    if (briefId) {
      shoutPlusFilters.briefId = briefId;
    }

    const trimmedQuery = queryText?.trim();
    if (trimmedQuery && !shoutPlusFilters.searchTerm) {
      shoutPlusFilters.searchTerm = trimmedQuery;
    }

    if (!shoutPlusFilters.sortField) {
      shoutPlusFilters.sortField = 'creator.average_rating';
      shoutPlusFilters.sortOrder = 'DESC';
    }

    shoutPlusFilters.limitModerationStatus = null;

    return shoutPlusFilters;
  }

  kickOffBlueSearch(filters?: any, isPagination: boolean = false, isDefault: boolean = false) {
    this.cancelInFlightBlueSearch();
    const mergedFilters = this.applyFindCreatorsModerationFilter(
      this.mergeSocialMonitoringBriefIntoBlueFilters(filters) ?? {}
    );
    console.log('💙 >> Kick off Blue Search...');
    this.purpleSearchResults.set(undefined);
    this.pinkSearchResults.set(undefined);

    if (mergedFilters) {
      console.log('💙 >> Kick off Blue - Filters:', mergedFilters);

      if (mergedFilters.searchTerm) {
        const uidRegex = /^[a-zA-Z0-9]{28}$/;
        if (uidRegex.test(mergedFilters.searchTerm)) {
          this.selectedPointer.set({ label: 'firebase_uid:', class: 'bg-blue-50 dark:bg-blue-900/30 text-blue-500 dark:text-blue-400 ring-blue-700', description: 'Search by User ID', help: 'Search by user id' });
        } else {
          this.selectedPointer.set({ label: 'name:', class: 'bg-blue-50 dark:bg-blue-900/30 text-blue-500 dark:text-blue-400 ring-blue-700', description: 'Search by name', help: 'Search by name' })
        }
      }
    }
    console.log('💙 >> Applied Search Term:', this.appliedSearchTerm);
    console.log('💙 >> Search Query:', this.searchQuery);
    console.log('💙 >> Is Pagination:', isPagination);
    console.log('💙 >> Current Page Number:', this.pageNumber());

    // Helper function to compare filters deeply
    const filtersChanged = (prevFilters: any, newFilters: any): boolean => {
      if (!prevFilters && !newFilters) return false;
      if (!prevFilters || !newFilters) return true;
      return JSON.stringify(prevFilters) !== JSON.stringify(newFilters);
    };

    if (!isPagination) {
      this.loadingResults.set(true);
    }

    // Check if search query changed
    const searchQueryChanged = this.appliedSearchTerm !== this.searchQuery();

    // Check if filters changed
    const filtersChangedValue = filtersChanged(this.previousFilters, mergedFilters);

    // Reset page number if search query or filters changed (unless it's pagination with no changes)
    if (searchQueryChanged || filtersChangedValue) {
      this.pageNumber.set(0);
      this.blueSearchResultsScrollTop = 0;
      this.blueSearchResults.set(undefined);
    } else if (isPagination) {
      // Pagination with no changes - pageNumber was already incremented in component, keep it
      console.log('💙 >> Pagination - keeping incremented page number:', this.pageNumber);
    } else {
      // New search but no changes detected, reset to 0
      this.pageNumber.set(0);
      this.blueSearchResultsScrollTop = 0;
      this.blueSearchResults.set(undefined);
    }

    // Update applied values
    this.appliedSearchTerm = this.searchQuery();
    this.searchColour.set('blue');
    if (mergedFilters !== undefined && mergedFilters !== null) {
      this.previousFilters = mergedFilters;
      this.appliedFilters = mergedFilters;
    }

    if ((this.selectedPointer() && this.searchQuery() && this.searchQuery()!.length > 2) || mergedFilters) {
      console.log('1. Before the TRY: ', mergedFilters);
      console.log('2. isDefault: ', isDefault);
      this.isSearching.set(true);

      this.blueSearchRequest = this.blue_search(
        this.selectedPointer()?.label || '',
        this.searchQuery()! || '',
        // this.searchMode() == 'filters' || !isDefault ? filters : undefined,
        mergedFilters,
        this.pageSize(),
        this.pageNumber() * this.pageSize()
      ).pipe(take(1)).subscribe((blue_search_response: any) => {
        console.log('💙 >> Blue Search Response:', blue_search_response);
        const newResults = blue_search_response.data as CreatorResponseDto[];
        if (this.blueSearchResults() && this.blueSearchResults()!.length > 0) {
          this.blueSearchResults.update(results => [...results!, ...newResults]);
        } else {
          this.blueSearchResults.set(newResults);
        }
        this.trackSearchImpressionsFromCreators(newResults);
        this.isSearching.set(false);
        this.loadingResults.set(false);
        this.blueSearchRequest = undefined;
      }, error => {
        this.isSearching.set(false);
        this.loadingResults.set(false);
        this.blueSearchRequest = undefined;
        console.error('💙 >> Blue Search Error:', error);
      })
    }
  }


  async kickOffPinkSearchForMe(refresh: boolean = false) {
    console.log('🩷 >> Kick off Pink Search for Me... ---> ON');
    this.blueSearchResults.set(undefined);
    this.purpleSearchResults.set(undefined);
    this.pinkSearchResults.set(undefined);

    this.pink_search_for_me().pipe(take(1)).subscribe(pink_search_for_me_response => {
      console.log('🩷 >> Pink Search for Me Response:', pink_search_for_me_response);
      const results = pink_search_for_me_response as CreatorResponseDto[];
      this.forYouSearchResults.set(results);
      this.trackSearchImpressionsFromCreators(results);

    }, error => {
      console.error('🩷 >> Pink Search for Me Error:', error);
    })

  }

  kickOffPinkSearch() {
    console.log('🩷 >> Kick off Pink Search...');
    console.log('🩷 >> Search Query:', this.searchQuery());
    console.log('🩷 >> Selected Pointer:', this.selectedPointer());
    if (this.selectedPointer() && this.searchQuery() && this.searchQuery()!.length > 2) {
      console.log('🩷 >> Kick off Pink Search... ---> ON .... ✅');
      this.isSearching.set(true);
      this.loadingResults.set(true);

      this.blueSearchResults.set(undefined);
      this.purpleSearchResults.set(undefined);
      this.pinkSearchResults.set(undefined);

      this.pink_search(this.selectedPointer()?.label, this.searchQuery() || '').pipe(take(1)).subscribe(pink_search_response => {
        console.log('🩷 >> Pink Search Response:', pink_search_response);
        this.loadingResults.set(false);
        this.isSearching.set(false);
        const results = pink_search_response as CreatorResponseDto[];
        this.pinkSearchResults.set(results);
        this.trackSearchImpressionsFromCreators(results);
      }, error => {
        console.error('🩷 >> Pink Search Error:', error);
      })
    } else {
      console.log('🩷 >> Kick off Pink Search... ---> OFF .... xxxx');
    }
  }

  kickOffPurpleSearch() {
    console.log('💜 >> Kick off Purple Search...');
    console.log('💜 >> Search Query:', this.searchQuery());
    console.log('💜 >> Selected Pointer:', this.selectedPointer());

    if (this.selectedPointer() && this.searchQuery() && this.searchQuery()!.length > 0) {
      this.isSearching.set(true);
      this.blueSearchResults.set(undefined);
      this.purpleSearchResults.set(undefined);
      this.pinkSearchResults.set(undefined);
      this.loadingResults.set(true);

      this.purple_search(this.selectedPointer().label, this.searchQuery() || '').pipe(take(1)).subscribe(purple_search_response => {
        console.log('💜💜💜 Purple Search Response:', purple_search_response);
        const results = purple_search_response as PurpleCreatorCardVideo[];
        this.purpleSearchResults.set(results);
        this.trackSearchImpressionsFromCreators(
          results.map((result) => result.creator).filter((creator): creator is CreatorResponseDto => !!creator?.blazeFirebaseUid),
        );
        this.loadingResults.set(false);
        this.isSearching.set(false);
      }, error => {
        console.error('🚨💜 Error kicking off Purple Search:', error);
        this.loadingResults.set(false);
        this.isSearching.set(false);
      })

    } else {
      // this._notificationService.sendMessage({
      //   message: 'Please provide a search query.',
      //   type: NotificationType.error
      // });
      // this.loadingResults = false;
    }
  }

  /**
   * Pink Search
   * Feeling lucky Search. 
   * 
   * Here the user can type in what ever they want.
   * Use GPT to form the Filter Object as well as a vector query.
   */
  pink_search(searchPointer: string, queryText: string) {
    const pink_search_url = `${environment.shout_api.url}/vector-search/pink`;
    return this.http.post(pink_search_url, { searchPointer, queryText }, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  /**
   * Pink Search
   * For Me Search. 
   * 
   * Here we will just send the brand ID to the server.
   * There we will use the brand info and products to find keywords to search for.
   */
  pink_search_for_me() {
    const pink_search_for_me_url = `${environment.shout_api.url}/vector-search/pink-for-me`;
    return this.http.post(pink_search_for_me_url, { brandId: this.currentUserService.focussedBrand.id }, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  /**
   * Blue Search
   * Standard psql Search.
   *
   * Search by name, email, handle, user id.
   * For SHOUT+ brief invites, uses business creators endpoint instead of marketplace blue search.
   */
  blue_search(searchPointer: string, queryText: string, filter?: any, take?: number, skip?: number) {
    console.log('Service http calls - filter: ', filter);

    if (this.isShoutPlusFindCreatorsAudience()) {
      const shoutPlusFilters = this.buildShoutPlusInviteFilters(filter, take, skip, queryText);
      console.log('💙 >> SHOUT+ invite creators filters:', shoutPlusFilters);
      return this.creatorService.filterShoutPlusCreators(shoutPlusFilters);
    }

    const update_preferences_url = `${environment.shout_api.url}/vector-search/blue/${take}/${skip}`;
    return this.http.post(update_preferences_url, { searchPointer, queryText, filter }, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  /**
   * Purple Search
   * Strictly a Vector Search
   * 
   * Describe a creator's persona.
   * Describe a scene in a video.
   */
  purple_search(searchPointer: string, queryText: string): Observable<PurpleCreatorCardVideo[]> {
    const purple_search_url = `${environment.shout_api.url}/vector-search/purple`;
    return this.http.post<PurpleCreatorCardVideo[]>(purple_search_url, { searchPointer, queryText }, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }



  kickOffGoldSearch(query: string, skip = 0, limit = 20, briefId?: string): Observable<GoldSearchResponseDto> {
    console.log('💛 >> Kick off Gold Search... --> ', query, `skip=${skip} limit=${limit}`);

    const gold_search_request: GoldSearchRequestDto = {
      queryText: query,
      limit,
      skip,
      briefId: briefId || undefined,
    };

    const gold_search_url = `${environment.shout_api.url}/vector-search/gold`;
    return this.http.post<GoldSearchResponseDto>(gold_search_url, gold_search_request, {
      headers: {
        'Content-Type': 'application/json',
      }
    }).pipe(
      tap((response) => {
        const creators = response.results
          .map((result) => result.creator as CreatorResponseDto)
          .filter((creator): creator is CreatorResponseDto => !!creator?.blazeFirebaseUid);
        this.trackSearchImpressionsFromCreators(creators);
      }),
    );
  }

  kickOffGoldSearchOnboarding(query: string, skip = 0, limit = 20, briefId?: string) {
    console.log('💛 >> Kick off Gold Search ONBOARDING... --> ', query, `skip=${skip} limit=${limit}`);

    const gold_search_request: GoldSearchRequestDto = {
      queryText: query,
      limit: 10,
      skip: 0,
      briefId: briefId || undefined,
    };

    const gold_search_url = `${environment.shout_api.url}/vector-search/gold`;
    this.http.post<GoldSearchResponseDto>(gold_search_url, gold_search_request, {
      headers: {
        'Content-Type': 'application/json',
      }
    }).subscribe((gold_search_response: any) => {
      console.log('💛 >> Gold Search ONBOARDING Response:', gold_search_response);
      this.goldOnboardingSearchResults.set(gold_search_response as GoldSearchResponseDto);
      this.loadingResults.set(false);
      this.isSearching.set(false);
    }, error => {
      console.error('💛 >> Gold Search ONBOARDING Error:', error);
      this.loadingResults.set(false);
      this.isSearching.set(false);
    });
  }

  private trackSearchImpressionsFromCreators(creators: CreatorResponseDto[]): void {
    const creatorIds = [...new Set(creators.map((creator) => creator.blazeFirebaseUid).filter(Boolean))];
    if (creatorIds.length === 0) {
      return;
    }

    this.creatorStatsService
      .trackSearchImpressions(creatorIds, this.currentUserService.focussedBrand?.id)
      .pipe(take(1))
      .subscribe({
      error: (error) => console.error('Failed to track search impressions:', error),
    });
  }
}

export interface SearchPointer {
  label: string,
  class: string,
  description: string,
  help: string
}

export interface GoldSearchRequestDto {
  queryText: string;
  limit?: number;  // default 20
  skip?: number;   // default 0
  weights?: GoldSearchWeightsDto;
  briefId?: string;
}

export interface GoldSearchWeightsDto {
  vectorRelevance?: number; // 0-1, default 0.40
  recommendation?: number;  // 0-1, default 0.20
  rating?: number;          // 0-1, default 0.15
  speed?: number;           // 0-1, default 0.10
  recency?: number;         // 0-1, default 0.10
  reviewCount?: number;     // 0-1, default 0.05
}

export interface GoldSearchScoreBreakdown {
  vectorRelevance: number;
  recommendation: number;
  rating: number;
  speed: number;
  recency: number;
  reviewCount: number;
}

export interface GoldSearchReviewsSummary {
  recommend: number;
  average_rating: number;
  total_reviews: number;
}

export interface GoldSearchCreatorResult {
  creator: any;
  compositeScore: number;
  scoreBreakdown: GoldSearchScoreBreakdown;
  evidence: GoldSearchEvidence;
  reviews_summary: GoldSearchReviewsSummary;
  briefApplication: BriefApplicationResponseDto;
}

export interface GoldSearchMatchedVideo {
  videoFile: any;
  score: number;
  source: string; // collection name
}

export interface GoldSearchEvidence {
  matchedVideos: GoldSearchMatchedVideo[];
  accountSummaryMatches: GoldSearchAccountMatch[];
}

export interface GoldSearchAccountMatch {
  source: string; // 'instagram_account_summary' | 'tiktok_account_summary'
  score: number;
}

export interface GoldSearchMeta {
  total: number;
  query: string;
  vectorQuery: string;
  filtersApplied: any;
  searchDurationMs: number;
  collectionsSearched: string[];
}

export interface GoldSearchResponseDto {
  results: GoldSearchCreatorResult[];
  meta: GoldSearchMeta;
}